import {
  Users,
  FileText,
  Calendar,
  AlertCircle,
  HardDrive,
  Bell,
  Server,
  BarChart3,
  Shield,
  Database,
  Clock
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { adminService } from '../../services/adminService'
import { toast } from 'sonner'

const AdminDashboard = () => {
  // Fetch system statistics
  const { data: stats, error } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminService.getStats(),
    refetchInterval: 30000, // refresh every 30 seconds
  })

  if (error) {
    toast.error('Failed to load admin statistics')
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'bg-primary-500',
      description: 'Registered users',
      change: '+2 this week',
    },
    {
      title: 'Active Users',
      value: stats?.active_users || 0,
      icon: Shield,
      color: 'bg-success-500',
      description: 'Logged in last 7 days',
      change: '85% active rate',
    },
    {
      title: 'Total Cases',
      value: stats?.total_cases || 0,
      icon: FileText,
      color: 'bg-warning-500',
      description: 'All cases created',
      change: '+5 this month',
    },
    {
      title: 'Open Cases',
      value: stats?.open_cases || 0,
      icon: AlertCircle,
      color: 'bg-danger-500',
      description: 'Currently open',
      change: '12% resolution rate',
    },
    {
      title: 'Total Documents',
      value: stats?.total_documents || 0,
      icon: Database,
      color: 'bg-info-500',
      description: 'Documents stored',
      change: '+128 this week',
    },
    {
      title: 'Storage Used',
      value: stats?.storage_used_bytes ? formatBytes(stats.storage_used_bytes) : '0 B',
      icon: HardDrive,
      color: 'bg-purple-500',
      description: `of ${stats?.storage_quota_bytes ? formatBytes(stats.storage_quota_bytes) : '∞'}`,
      change: '65% of quota',
    },
    {
      title: 'Upcoming Deadlines',
      value: stats?.upcoming_deadlines || 0,
      icon: Calendar,
      color: 'bg-orange-500',
      description: 'Within next 7 days',
      change: '2 high priority',
    },
    {
      title: 'Missed Deadlines',
      value: stats?.missed_deadlines || 0,
      icon: AlertCircle,
      color: 'bg-red-500',
      description: 'Overdue',
      change: 'Require attention',
    },
    {
      title: 'Unread Notifications',
      value: stats?.unread_notifications || 0,
      icon: Bell,
      color: 'bg-indigo-500',
      description: 'Pending notifications',
      change: '12% increase',
    },
    {
      title: 'System Uptime',
      value: stats?.system_uptime ? formatUptime(stats.system_uptime) : '0 days',
      icon: Server,
      color: 'bg-green-500',
      description: 'Continuous operation',
      change: '99.8% availability',
    },
  ]

  const systemHealth = [
    { component: 'Database', status: 'healthy', lastCheck: '2 minutes ago' },
    { component: 'MinIO Storage', status: 'healthy', lastCheck: '5 minutes ago' },
    { component: 'NATS Queue', status: 'degraded', lastCheck: '10 minutes ago' },
    { component: 'Redis Cache', status: 'healthy', lastCheck: '1 minute ago' },
    { component: 'Email Service', status: 'healthy', lastCheck: '15 minutes ago' },
    { component: 'WebSocket', status: 'healthy', lastCheck: '30 seconds ago' },
  ]

  const recentActivities = [
    { id: 1, user: 'System', action: 'Nightly backup completed', target: '', time: '2 hours ago', type: 'backup' },
    { id: 2, user: 'admin@example.com', action: 'Created new user', target: 'jane.doe@example.com', time: '3 hours ago', type: 'user' },
    { id: 3, user: 'cron', action: 'Deadline check executed', target: '3 notifications sent', time: '4 hours ago', type: 'cron' },
    { id: 4, user: 'system', action: 'Storage quota warning', target: '85% of limit', time: '5 hours ago', type: 'storage' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600">System overview and health monitoring</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
            Generate Report
          </button>
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.title}
            className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div className={`p-2 rounded-lg ${card.color}`}>
                <card.icon className="h-5 w-5 text-white" />
              </div>
              <div className="text-sm text-gray-500">{card.change}</div>
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold text-gray-900">{card.value}</div>
              <div className="text-sm font-medium text-gray-700">{card.title}</div>
              <div className="text-xs text-gray-500 mt-1">{card.description}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Health */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">System Health</h2>
            <span className="text-sm text-gray-500">Last updated: Just now</span>
          </div>
          <div className="space-y-3">
            {systemHealth.map((health) => (
              <div key={health.component} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  <div className={`h-3 w-3 rounded-full ${health.status === 'healthy' ? 'bg-green-500' : health.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'}`}></div>
                  <span className="font-medium text-gray-800">{health.component}</span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${health.status === 'healthy' ? 'bg-green-100 text-green-800' : health.status === 'degraded' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                    {health.status}
                  </span>
                  <span className="text-sm text-gray-500">{health.lastCheck}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent System Activities</h2>
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                <div className={`p-2 rounded-lg ${activity.type === 'backup' ? 'bg-blue-100' : activity.type === 'user' ? 'bg-green-100' : activity.type === 'cron' ? 'bg-purple-100' : 'bg-yellow-100'}`}>
                  {activity.type === 'backup' && <Server className="h-4 w-4 text-blue-600" />}
                  {activity.type === 'user' && <Users className="h-4 w-4 text-green-600" />}
                  {activity.type === 'cron' && <Clock className="h-4 w-4 text-purple-600" />}
                  {activity.type === 'storage' && <HardDrive className="h-4 w-4 text-yellow-600" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">{activity.user}</span>
                    <span className="text-xs text-gray-500">{activity.time}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">{activity.action}</span>
                    {activity.target && <span className="text-gray-800"> {activity.target}</span>}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <button className="mt-4 w-full py-2 text-sm text-primary-600 hover:text-primary-800 font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            View All Activities
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <Users className="h-8 w-8 text-primary-600 mb-2" />
            <span className="font-medium text-gray-800">Manage Users</span>
            <span className="text-xs text-gray-500">Add, edit, delete users</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <HardDrive className="h-8 w-8 text-primary-600 mb-2" />
            <span className="font-medium text-gray-800">Storage Report</span>
            <span className="text-xs text-gray-500">Analyze storage usage</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <BarChart3 className="h-8 w-8 text-primary-600 mb-2" />
            <span className="font-medium text-gray-800">Audit Logs</span>
            <span className="text-xs text-gray-500">View system logs</span>
          </button>
          <button className="flex flex-col items-center justify-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
            <Server className="h-8 w-8 text-primary-600 mb-2" />
            <span className="font-medium text-gray-800">Backup Now</span>
            <span className="text-xs text-gray-500">Create system backup</span>
          </button>
        </div>
      </div>
    </div>
  )
}

// Helper functions
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days > 0) {
    return `${days}d ${hours}h`
  }
  return `${hours}h`
}

export default AdminDashboard